import React from "react";

import {
  formatCalendarDay,
  formatTemporal,
  isTemporal,
} from "@/utils/temporal";

// Derive the <input type="date"> value (YYYY-MM-DD) from a cell's value.
//
// The value states its own granularity (#11055): a calendar date gives up its
// day verbatim, an instant gives the day it falls on in the zone it names. That
// is the same rule the cell renderer applies (`Components/dateScalar.ts`), so
// opening a cell for edit can never show a different day than the one on
// screen -- which is what this used to do, by reading every timestamp in UTC.
//
// Anything that is not a day comes back empty: `<input type="date">` cannot
// display it, and a blank field beats a silently rejected value.
export const formatDateForInput = (value: unknown): string => {
  if (value == null) return "";
  const day = isTemporal(value)
    ? formatTemporal(value)
    : formatCalendarDay(String(value));
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : "";
};

interface DateInputProps {
  value: unknown;
  onChange: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  disabled?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  inputRef,
  disabled = false,
}) => {
  return (
    <input
      ref={inputRef}
      type="date"
      value={formatDateForInput(value)}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      // Token anatomy, native function (#11426 component sweep, D2): the
      // picker stays the browser's, but the field speaks the panel's input
      // grammar — token border/ink/ground instead of the raw gray chrome,
      // and the calendar glyph rests quiet.
      className={`w-full px-3 py-1.5 rounded-md text-sm text-[var(--fp-ink)] border border-[var(--fp-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)] [color-scheme:light] [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 ${
        disabled
          ? "bg-[var(--fp-band)] cursor-not-allowed"
          : "bg-[var(--fp-surface)]"
      }`}
    />
  );
};

export default DateInput;
